import { TextKey } from '@mews-commander/enums';
import { LocalizedText } from '@mews/framework';
import { Button, Dialog, Stack } from '@optimus-web/core';
import { PasswordField } from '@mews-commander/form';
import { LocalizedLabel, useDialogs } from '@mews-commander/ui';
import { SuccessDialog } from '../SuccessDialog';
import { ChangePasswordFields } from './types';
import { useChangePasswordForm } from './useChangePasswordForm';
import { CHANGE_PASSWORD_FORM_ID } from './constants';

export const CHANGE_PASSWORD_DIALOG_NAME = 'changePassword';

export const ChangePasswordDialog = ({ password: oldPassword }: { password: string }) => {
    const { closeDialog } = useDialogs();
    const { isLoading, renderFormContent, isSuccess } = useChangePasswordForm(oldPassword);

    const onClose = () => closeDialog(CHANGE_PASSWORD_DIALOG_NAME);

    return isSuccess ? (
        <Success onClose={onClose} />
    ) : (
        <NewPasswordForm isLoading={isLoading} onClose={onClose} renderForm={renderFormContent} />
    );
};

const NewPasswordForm = ({
    isLoading,
    onClose,
    renderForm,
}: {
    isLoading: boolean;
    onClose: () => void;
    renderForm: (children: React.ReactNode) => React.ReactNode;
}) => (
    <Dialog
        size="small"
        active
        loading={isLoading}
        title={<LocalizedText textKey={TextKey.ChangePassword} />}
        onClose={onClose}
        footer={{
            secondary: (
                <Button type="button" onClick={onClose} variant="tertiary">
                    <LocalizedText textKey={TextKey.Cancel} />
                </Button>
            ),
            primary: (
                <Button type="submit" form={CHANGE_PASSWORD_FORM_ID} variant="primary">
                    <LocalizedText textKey={TextKey.Save} />
                </Button>
            ),
        }}
    >
        {renderForm(
            <Stack vertical spacing="200">
                <LocalizedLabel variant="secondary" textKey={TextKey.EnterNewPassword} as="div" />

                <PasswordField
                    name={ChangePasswordFields.NewPassword}
                    label={<LocalizedText textKey={TextKey.NewPassword} />}
                    helper={<LocalizedText textKey={TextKey.PasswordRequirementsErrorMessage} />}
                    hasPasswordVisibilityToggle
                    showPasswordStrength
                    required
                />

                <PasswordField
                    name={ChangePasswordFields.ConfirmNewPassword}
                    label={<LocalizedText textKey={TextKey.ConfirmNewPassword} />}
                    autoComplete="new-password"
                    required
                />
            </Stack>
        )}
    </Dialog>
);

const Success = ({ onClose }: { onClose: () => void }) => (
    <SuccessDialog
        onClose={onClose}
        titleTextKey={TextKey.ChangePasswordDialogSuccessTitle}
        descriptionTextKey={TextKey.ChangePasswordDialogSuccess}
    />
);
